import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import {
  IconExclamationCircle,
  IconPlayerPlayFilled,
} from "@tabler/icons-react";
import type {
  DfyDeliveryDto,
  DfyEditorVideoRenderDto,
  DfyEditorVideoRendersScope,
} from "@repo/api/dfy";
import { DfyApi } from "@/lib/api/dfy-api";
import { usePagedDfyEditorVideoRendersQuery } from "@/lib/queries/dfy-queries";
import { formatCreatedAt } from "@/lib/utils";
import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import Tabs from "@/lib/components/Tabs";
import Toast from "@/lib/components/Toast";
import { Badge } from "@/lib/components/Badge";
import { getQualityLabel } from "@/features/Editor/Navbar/ExportVideoDialog/EarlierExportsView";

const TAB_OPTIONS: { label: string; value: DfyEditorVideoRendersScope }[] = [
  { label: "This order", value: "folder" },
  { label: "All renders", value: "all" },
];

// Picks a finished render from the editor team to attach to an order as a delivery.
export default function RenderPickerDialog({
  open,
  onOpenChange,
  teamExternalId,
  orderExternalId,
  addedRenderExternalIds,
  onAdded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  teamExternalId: string;
  orderExternalId: string;
  addedRenderExternalIds: string[];
  onAdded: (delivery: DfyDeliveryDto) => void;
}) {
  const [tab, setTab] = useState<DfyEditorVideoRendersScope>("folder");
  const addedExternalIds = new Set(addedRenderExternalIds);

  const addMutation = useMutation({
    mutationFn: (videoRenderExternalId: string) =>
      DfyApi.createDelivery(teamExternalId, orderExternalId, {
        videoRenderExternalId,
      }),
    onSuccess: onAdded,
    onError: () => Toast.error("Failed to add render"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-156 max-w-xl gap-0"
        classNameInner="flex flex-col gap-0 overflow-hidden p-0 sm:px-0"
        showCloseButton={false}
      >
        <DialogHeader variant="bordered" showCloseButton>
          <DialogTitle>Add a render</DialogTitle>
        </DialogHeader>
        <Tabs
          currentTab={tab}
          onTabClicked={setTab}
          options={TAB_OPTIONS}
          classNameOptions="pt-4"
          renderCurrentTab={(currentTab) => (
            <RenderList
              teamExternalId={teamExternalId}
              orderExternalId={orderExternalId}
              scope={currentTab}
              pendingExternalId={
                addMutation.isPending ? addMutation.variables : undefined
              }
              addedExternalIds={addedExternalIds}
              onAdd={(externalId) => {
                if (addedExternalIds.has(externalId)) {
                  Toast.error("This render is already added to the order");
                  return;
                }
                addMutation.mutate(externalId);
              }}
            />
          )}
        />
      </DialogContent>
    </Dialog>
  );
}

function RenderList({
  teamExternalId,
  orderExternalId,
  scope,
  pendingExternalId,
  addedExternalIds,
  onAdd,
}: {
  teamExternalId: string;
  orderExternalId: string;
  scope: DfyEditorVideoRendersScope;
  pendingExternalId?: string;
  addedExternalIds: Set<string>;
  onAdd: (videoRenderExternalId: string) => void;
}) {
  const { ref, inView } = useInView();
  const {
    data,
    isLoading,
    isError,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    hasNextPage,
    fetchNextPage,
  } = usePagedDfyEditorVideoRendersQuery(
    teamExternalId,
    orderExternalId,
    scope,
  );

  useEffect(() => {
    if (inView && !isFetching && !isError && hasNextPage) {
      void fetchNextPage();
    }
  }, [inView, isFetching, isError, hasNextPage, fetchNextPage]);

  const renders = data?.pages.flatMap((page) => page.videoRenders) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (isError && !data) {
    return (
      <div
        role="alert"
        className="flex h-full items-center justify-center gap-2 px-6"
      >
        <IconExclamationCircle
          size={24}
          className="shrink-0 text-error"
          aria-hidden="true"
        />
        <p className="font-medium">Failed to load renders</p>
      </div>
    );
  }

  if (renders.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <p className="text-sm text-text-secondary">
          {scope === "folder"
            ? "No finished renders in this order's folder yet."
            : "No finished renders in this team yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto px-6 py-4">
      <ul className="flex flex-col gap-2">
        {renders.map((item) => (
          <RenderRow
            key={item.videoRender.externalId}
            item={item}
            showFolderBadge={scope === "all"}
            isAdded={addedExternalIds.has(item.videoRender.externalId)}
            isPending={pendingExternalId === item.videoRender.externalId}
            isDisabled={pendingExternalId !== undefined}
            onAdd={() => onAdd(item.videoRender.externalId)}
          />
        ))}
      </ul>
      <div ref={ref} className="flex min-h-12 items-center justify-center">
        {isFetchingNextPage ? (
          <LoadingSpinner size={24} />
        ) : isFetchNextPageError ? (
          <div role="alert" className="flex items-center gap-2">
            <IconExclamationCircle
              size={20}
              className="shrink-0 text-error"
              aria-hidden="true"
            />
            <p className="text-sm font-medium">Failed to load more renders</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RenderRow({
  item,
  showFolderBadge,
  isAdded,
  isPending,
  isDisabled,
  onAdd,
}: {
  item: DfyEditorVideoRenderDto;
  showFolderBadge: boolean;
  isAdded: boolean;
  isPending: boolean;
  isDisabled: boolean;
  onAdd: () => void;
}) {
  const { videoRender, isInOrderFolder } = item;

  return (
    <li className="flex items-center gap-3 rounded-lg border border-border p-3">
      <RenderPreview src={videoRender.downloadUrl} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate text-xs font-medium">
          {videoRender.externalFileName}
        </span>
        <span className="truncate text-xs text-text-secondary">
          {formatCreatedAt(videoRender.createdAt, null)}
        </span>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {videoRender.aspectRatio && (
            <Badge size="xs" variant="outline">
              {videoRender.aspectRatio}
            </Badge>
          )}
          <Badge size="xs" variant="outline">
            {getQualityLabel(videoRender.upscaleQuality)}
          </Badge>
          {showFolderBadge && isInOrderFolder && (
            <Badge size="xs" variant="outline">
              This order
            </Badge>
          )}
        </div>
      </div>
      <Button
        size="sm"
        className="shrink-0"
        variant={isAdded ? "outline" : "default"}
        isLoading={isPending}
        disabled={isDisabled || isAdded}
        onClick={onAdd}
      >
        {isAdded ? "Added" : "Add"}
      </Button>
    </li>
  );
}

// Only the metadata and first frame are fetched until the user presses play.
// The #t fragment makes Safari render the first frame with preload="metadata".
function RenderPreview({ src }: { src: string | null }) {
  const { ref, inView } = useInView({ triggerOnce: true });
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hasStarted, setHasStarted] = useState(false);

  return (
    <div
      ref={ref}
      className="relative aspect-video w-48 shrink-0 overflow-hidden rounded-md bg-black"
    >
      {inView && src && (
        <>
          <video
            ref={videoRef}
            src={`${src}#t=0.1`}
            preload="metadata"
            controls={hasStarted}
            playsInline
            className="h-full w-full object-contain"
          />
          {!hasStarted && (
            <button
              type="button"
              onClick={() => {
                setHasStarted(true);
                videoRef.current?.play().catch(() => {});
              }}
              aria-label="Play preview"
              className="absolute inset-0 flex items-center justify-center"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/50 text-white">
                <IconPlayerPlayFilled size={16} />
              </span>
            </button>
          )}
        </>
      )}
    </div>
  );
}
